package com.siriusxm.pia.views.unifiedaggregator.events

import androidx.compose.runtime.Composable
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.EntityDiffStyles
import kotlinx.serialization.json.JsonObject
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Text

/**
 * Modal dialog for comparing a rejected entity with its authorized version.
 *
 * Displays two view modes:
 * - Differences Only: Shows only the fields that differ
 * - Side by Side: Full entities displayed in two columns
 *
 * @param comparisonResult The comparison data
 * @param onClose Callback when the dialog is closed
 */
@Composable
fun EntityComparisonDialog(
    comparisonResult: EntityComparisonResult,
    onClose: () -> Unit
) {
    Style(EntityDiffStyles)

    dialogOverlay {
        title = "Compare Rejected Update with Authorized Version"

        onClose {
            onClose()
        }

        content {
            Div({
                style {
                    padding(20.px)
                    minHeight(400.px)
                    maxHeight(70.vh)
                    overflow("auto")
                }
            }) {
                // Entity info header with badges
                Div({
                    style {
                        marginBottom(16.px)
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        alignItems(AlignItems.Center)
                        flexWrap(FlexWrap.Wrap)
                    }
                }) {
                    Badge(BadgeColor.BLUE, fontSize = 13.px) {
                        Text(comparisonResult.entityType)
                    }

                    Badge(BadgeColor.GRAY, fontSize = 12.px) {
                        Text("v${comparisonResult.entityVersion}")
                    }

                    Div({
                        style {
                            fontSize(12.px)
                            color(SXMUI.subtleTextColor.value())
                            fontFamily("monospace")
                        }
                    }) {
                        Text("ID: ${comparisonResult.entityId}")
                    }
                }

                if (comparisonResult.authorized != null) {
                    ComparisonViewTabs(comparisonResult)
                } else if (comparisonResult.referenceEntity != null) {
                    ReferenceComparisonView(comparisonResult)
                } else {
                    NoAuthorizedVersionView(comparisonResult.rejected)
                }
            }
        }
    }
}

/**
 * Displays tabs for switching between different comparison view modes.
 */
@Composable
private fun ComparisonViewTabs(comparisonResult: EntityComparisonResult) {
    tabView {
        selection = "differences"

        tab("Differences Only", "differences") {
            DifferencesOnlyView(comparisonResult.diffs)
        }

        tab("Side by Side", "sidebyside") {
            Div({
                style {
                    marginTop(16.px)
                }
            }) {
                SideBySideView(
                    authorized = comparisonResult.authorized,
                    rejected = comparisonResult.rejected
                )
            }
        }
    }
}

/**
 * Displays the comparison when no authorized version exists for this entity,
 * but a valid reference entity of the same type was found. Shows the differences
 * between the reference and rejected payloads so the user can spot issues.
 */
@Composable
private fun ReferenceComparisonView(comparisonResult: EntityComparisonResult) {
    messageBox(type = MessageType.INFO) {
        Div {
            Div({
                style {
                    fontWeight(600)
                    marginBottom(8.px)
                }
            }) {
                Text("No published version for this entity")
            }

            Div({
                style {
                    fontSize(14.px)
                }
            }) {
                Text("Comparing against a valid ${comparisonResult.entityType} entity as a reference. Differences may include expected field variations (e.g., id, name) in addition to the issue that caused rejection.")
            }
        }
    }

    tabView {
        selection = "differences"

        tab("Differences", "differences") {
            DifferencesOnlyView(comparisonResult.diffs)
        }

        tab("Side by Side", "sidebyside") {
            Div({
                style {
                    marginTop(16.px)
                }
            }) {
                SideBySideView(
                    authorized = comparisonResult.referenceEntity,
                    rejected = comparisonResult.rejected
                )
            }
        }
    }
}

/**
 * Displays a message when no authorized version exists,
 * along with the rejected entity for reference.
 */
@Composable
private fun NoAuthorizedVersionView(rejected: JsonObject) {
    messageBox(type = MessageType.WARNING) {
        Div {
            Div({
                style {
                    fontWeight(600)
                    marginBottom(8.px)
                }
            }) {
                Text("No Published Version Exists")
            }

            Div({
                style {
                    fontSize(14.px)
                }
            }) {
                Text("This entity has not been authorized for publication yet. The update below was rejected, possibly due to validation errors or authorization constraints.")
            }
        }
    }

    // Show the rejected entity for reference
    H3({
        style {
            margin(16.px, 0.px, 12.px, 0.px)
            fontSize(16.px)
        }
    }) {
        Text("Rejected Update:")
    }

    Div({
        classes(EntityDiffStyles.rejectedEntityContainer)
    }) {
        jsonView(rejected, hideEmptyOrNull = false)
    }
}
